import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Avatar,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  initialsFromName,
} from '@mlm/ui';
import type { Address } from '@mlm/shared';
import { patchMe, removeProfilePicture, uploadProfilePicture } from '../../lib/api';
import { useAuthStore } from '../../lib/auth-store';

/** One-line summary of a stored address for the read-only Profile display. */
function formatAddress(address?: Address | null): string {
  if (!address) return '';
  const parts = [
    address.line1,
    address.line2,
    address.barangay,
    address.city,
    address.province,
    address.region,
    address.postalCode,
  ].filter((p): p is string => Boolean(p && p.trim()));
  return parts.join(', ');
}

export function ProfileTab() {
  const user = useAuthStore((s) => s.user);
  const setSession = useAuthStore((s) => s.setSession);
  const token = useAuthStore((s) => s.token);
  const [firstName, setFirstName] = useState(user?.firstName ?? '');
  const [middleName, setMiddleName] = useState(user?.middleName ?? '');
  const [lastName, setLastName] = useState(user?.lastName ?? '');
  const [phone, setPhone] = useState(user?.phone ?? '');
  const [saved, setSaved] = useState(false);

  // Admin-controlled editability. Missing config (older session) = editable, so
  // behavior is unchanged until an admin locks a field.
  const editable = user?.tenantPolicy?.profileEditable;
  const nameEditable = editable?.name ?? true;
  const phoneEditable = editable?.phone ?? true;
  const photoEditable = editable?.profilePicture ?? true;
  // The Save button only writes name/phone; hide it when neither is editable.
  const canSave = nameEditable || phoneEditable;

  // Optimistic local preview of a just-picked file, shown until /me re-hydrates.
  const [preview, setPreview] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const avatarUrl = preview ?? user?.profilePictureUrl ?? undefined;

  const updateAvatar = (profilePictureUrl: string | null) => {
    if (token && user) setSession(token, { ...user, profilePictureUrl });
  };

  const pic = useMutation({
    mutationFn: (file: File) => uploadProfilePicture(file),
    onSuccess: ({ profilePictureUrl }) => {
      updateAvatar(profilePictureUrl);
      setPreview(null);
    },
    onError: () => setPreview(null),
  });

  const removePic = useMutation({
    mutationFn: () => removeProfilePicture(),
    onSuccess: () => {
      updateAvatar(null);
      setPreview(null);
    },
  });

  const onPick = (file: File | null) => {
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    pic.mutate(file);
  };

  const mut = useMutation({
    meta: { successMessage: 'Profile saved.' },
    mutationFn: () =>
      patchMe({
        // Only send fields the member is allowed to (and did) change. Middle
        // name can be explicitly cleared (null) since it's the only optional one.
        ...(nameEditable
          ? {
              firstName: firstName || undefined,
              middleName: middleName ? middleName : null,
              lastName: lastName || undefined,
            }
          : {}),
        ...(phoneEditable ? { phone: phone || null } : {}),
      }),
    onSuccess: (data) => {
      if (token && user) {
        setSession(token, {
          ...user,
          fullName: data.fullName,
          firstName: data.firstName,
          middleName: data.middleName,
          lastName: data.lastName,
          phone: data.phone,
        });
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    },
  });

  const address = formatAddress(user?.address);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Profile</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-4">
          <Avatar
            size="lg"
            className="h-16 w-16 text-xl"
            src={avatarUrl}
            initials={initialsFromName(user?.fullName ?? user?.email ?? 'ME', 'ME')}
          />
          {photoEditable ? (
            <div className="space-y-1.5">
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  onPick(e.target.files?.[0] ?? null);
                  e.target.value = '';
                }}
              />
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={pic.isPending}
                  onClick={() => fileRef.current?.click()}
                >
                  {pic.isPending ? 'Uploading…' : user?.profilePictureUrl ? 'Change photo' : 'Upload photo'}
                </Button>
                {user?.profilePictureUrl && !pic.isPending && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={removePic.isPending}
                    onClick={() => removePic.mutate()}
                  >
                    Remove
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">JPG or PNG, up to 5 MB.</p>
              {pic.error && <p className="text-xs text-destructive">Couldn't upload. Try again.</p>}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">Profile photo is managed by the admin.</p>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-muted-foreground">First name</label>
            <Input value={firstName} disabled={!nameEditable} onChange={(e) => setFirstName(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Middle name (optional)</label>
            <Input value={middleName} disabled={!nameEditable} onChange={(e) => setMiddleName(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Last name</label>
            <Input value={lastName} disabled={!nameEditable} onChange={(e) => setLastName(e.target.value)} />
          </div>
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Phone</label>
          <Input value={phone} disabled={!phoneEditable} onChange={(e) => setPhone(e.target.value)} />
        </div>

        {/* Read-only account details — shown for reference, not editable here. */}
        <div className="space-y-2 rounded-md border bg-muted/30 p-3">
          <ReadOnlyRow label="Email" value={user?.email} mono />
          {user?.username && <ReadOnlyRow label="Username" value={user.username} mono />}
          {address && <ReadOnlyRow label="Address" value={address} />}
          <p className="text-xs text-muted-foreground">
            Email and username can't be changed here — contact admin. Update your address from the
            Address tab.
          </p>
        </div>

        {canSave && (
          <Button onClick={() => mut.mutate()} disabled={mut.isPending} size="sm">
            {mut.isPending ? 'Saving…' : saved ? 'Saved' : 'Save'}
          </Button>
        )}
        {mut.error && <p className="text-xs text-destructive">Couldn't save. Try again.</p>}
      </CardContent>
    </Card>
  );
}

function ReadOnlyRow({ label, value, mono }: { label: string; value?: string | null; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <span className={mono ? 'font-mono text-right' : 'text-right'}>{value || '—'}</span>
    </div>
  );
}
